import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  resource,
  signal
} from '@angular/core';
import {DatePipe, JsonPipe} from "@angular/common";
import {ConfirmationService, MessageService, PrimeTemplate} from "primeng/api";
import {TableModule} from "primeng/table";
import {Tag} from "primeng/tag";
import {LabeledComponent} from "../../components/labeled/labeled.component";
import {UserService} from "../../services/user.service";
import {firstValueFrom, finalize} from "rxjs";
import {
  ApplicationDto,
  ClearIncomeNycIdCommand,
  EmitApplicationStatusEventCommand,
  ApartmentDto,
  ResetSyncStatusCommand,
  UserDto,
  VerifyNycHouseholdResultDto
} from "admin-api-dto";
import {TypedTableRowDirective} from "../../typed-table-row.directive";
import {
  applicationStatusToTag,
  homeyStatusToTag,
  nycProfileStatusToTag,
  paymentStatusToTag,
  subscriptionStatusToTag,
  syncStatusToTag
} from "../../enum-to-tag";
import {Button} from "primeng/button";
import {NycAuth} from "../../model/user.model";
import {CopyButtonComponent} from "../../components/copy-button/copy-button.component";
import {Dialog} from "primeng/dialog";
import {InputText} from "primeng/inputtext";
import {FormBuilder, ReactiveFormsModule} from "@angular/forms";
import {EmailService} from "../../services/email.service";
import {ApplicationService} from "../../services/application.service";
import {
  HouseholdMemberDetailsComponent
} from "../../components/household-member-details/household-member-details.component";
import {Router} from "@angular/router";
import {environment} from "../../environments/environment";
import {DropdownModule} from "primeng/dropdown";
import {ApartmentService} from "../../services/apartment.service";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";

@Component({
  selector: 'app-user-page',
  imports: [
    DatePipe,
    PrimeTemplate,
    TableModule,
    Tag,
    LabeledComponent,
    TypedTableRowDirective,
    Button,
    CopyButtonComponent,
    Dialog,
    InputText,
    ReactiveFormsModule,
    HouseholdMemberDetailsComponent,
    JsonPipe,
    DropdownModule,
  ],
  templateUrl: './user-page.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UserPageComponent {
  public readonly userId = input.required<string>();

  private readonly userService = inject(UserService);
  private readonly emailService = inject(EmailService);
  private readonly messageService = inject(MessageService);
  private readonly fb = inject(FormBuilder);
  private readonly applicationService = inject(ApplicationService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly router = inject(Router);
  private readonly apartmentService = inject(ApartmentService);
  private readonly destroyRef = inject(DestroyRef);

  public readonly user = resource<UserDto, { id: string }>({
    request: () => ({id: this.userId()}),
    loader: ({request: {id: id}}) => firstValueFrom(this.userService.getUser(id))
  })

  protected readonly subscriptionStatusToTag = subscriptionStatusToTag;
  protected readonly paymentStatusToTag = paymentStatusToTag;
  protected readonly nycProfileStatusToTag = nycProfileStatusToTag;
  protected readonly syncStatusToTag = syncStatusToTag;
  protected readonly applicationStatusToTag = applicationStatusToTag;
  protected readonly homeyStatusToTag = homeyStatusToTag;
  protected readonly nycProfileForm = computed(
    () => {
      const nycAuth = this.nycAuth();

      if (!nycAuth) return undefined;

      const formGroup = this.fb.group({
        email1: this.fb.nonNullable.control(nycAuth.email1),
        email2: this.fb.nonNullable.control(nycAuth.email2),
        username: this.fb.nonNullable.control(nycAuth.username),
        password: this.fb.nonNullable.control(nycAuth.password),
        firstName: this.fb.nonNullable.control(nycAuth.firstName),
        lastName: this.fb.nonNullable.control(nycAuth.lastName),
      });

      formGroup.disable();
      return formGroup
    }
  )
  protected readonly nycAuth = signal<NycAuth | undefined>(undefined)
  protected readonly registerInNycVisible = signal(false);
  protected editingNycProfile: boolean = false;
  protected readonly allowApplicationUpsert = environment.allowApplicationUpsert === true;
  protected readonly allowApplicationStatusEvent = environment.allowApplicationStatusEvent === true;
  protected readonly allowEligibleLotteryEvent = environment.allowEligibleLotteryEvent === true;
  protected readonly statusEventDialogVisible = signal(false);
  protected readonly eligibleEventDialogVisible = signal(false);
  protected readonly selectedApplication = signal<ApplicationDto | undefined>(undefined);
  protected readonly statusOptions = ['applied', 'pending-selection', 'not-selected', 'closed'] as const;
  protected readonly reasonOptions = ['not-selected', 'withdrawn', 'duplicate', 'not-eligible'] as const;
  protected readonly applicationMethodOptions = [
    'manual-apply',
    'auto-apply',
    'auto-apply-after-changes',
    'initial-auto-apply',
    'initial-manual-apply',
  ] as const;
  protected readonly tableScrollHeight = '360px';
  protected readonly statusEventForm = this.fb.group({
    status: this.fb.nonNullable.control<'applied' | 'pending-selection' | 'not-selected' | 'closed'>('applied'),
    lotteryNumber: this.fb.control<number | null>(null),
    reason: this.fb.control<'not-selected' | 'withdrawn' | 'duplicate' | 'not-eligible' | null>(null),
    applicationMethod: this.fb.control<ApplicationDto['method'] | null>(null)
  });
  protected readonly availableLotteries = signal<ApartmentDto[]>([]);
  protected readonly openLotteries = computed(() => this.availableLotteries().filter(lottery => {
    if (!lottery.endDate) {
      return true;
    }
    return new Date(lottery.endDate) > new Date();
  }));
  protected readonly displayOpenLotteries = computed(() => this.openLotteries().slice(0, 12));
  protected readonly selectedLotteryIds = signal<Set<string>>(new Set());
  protected readonly verifyingHousehold = signal(false);
  protected readonly householdVerificationResult = signal<VerifyNycHouseholdResultDto | undefined>(undefined);
  protected readonly syncingUserWithNyc = signal(false);
  protected readonly allowManualNycSync = computed(() => {
    const profile = this.user.value()?.nycProfile;
    return profile?.status === 'complete'
      && (profile.syncStatus === 'not-synced' || profile.syncStatus === 'sync-failed' || profile.syncStatus === 'synced');
  });

  constructor() {
    this.apartmentService.apartments$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(lotteries => this.availableLotteries.set(lotteries));
  }

  onRegisterInNYCClick() {
    this.userService.getNycAuth(this.user.value()!.id).subscribe(
      nycAuth => {
        this.nycAuth.set(nycAuth)
        this.registerInNycVisible.set(true)
        this.user.reload();
      }
    );
  }

  getNycAuth() {
    this.userService.getNycAuth(this.user.value()!.id).subscribe(
      nycAuth => this.nycAuth.set(nycAuth)
    );
  }

  resetLoginSyncResult() {
    this.userService.resetLoginSyncResult(this.user.value()!.id).subscribe(
      () => {
        this.user.reload();
        this.messageService.add({
          severity: 'success',
          summary: 'Reset login sync result',
          detail: `User ${this.user.value()!.email} login sync result reset`
        });
      }
    );
  }

  allowResetPassword() {
    this.userService.markResetPassword(this.user.value()!.id).subscribe(
      () => {
        this.user.reload();
        this.messageService.add({
          severity: 'success',
          summary: 'Reset password marked',
          detail: `User ${this.user.value()!.email} marked for reset password`
        });
      }
    );
  }

  linkAuthToOtherUser() {
    const confirmed = window.confirm('Are you sure you want to link this user auth to another user? And dont forget, Stripe for this user will be deleted, Revenuecat needs to be handled manually.');

    if (!confirmed) {
      return;
    }

    const targetUserId = window.prompt('Enter the user ID to link the auth to');

    if (!targetUserId) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Link auth cancelled',
        detail: 'No user ID was provided.'
      });
      return;
    }

    this.userService.linkAuthToOtherUser(
      this.user.value()!.id,
      {targetUserId}
    ).subscribe({
      next: (response) => {
        const detail = response.updatedMappings === 0
          ? 'No authentication mappings were found for this user.'
          : `Linked ${response.updatedMappings} authentication mapping(s) to ${targetUserId}.`;

        this.messageService.add({
          severity: response.updatedMappings === 0 ? 'info' : 'success',
          summary: response.deleted ? 'Link auth complete' : 'Link auth result',
          detail: response.deleted ? `${detail} Source user deleted.` : detail
        });

        this.router.navigate(['/users']);
      },
      error: (error) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Link auth failed',
          detail: error?.error?.message ?? 'Link auth to other user failed.'
        });
      }
    });
  }

  onMarkAsRegisteredClick() {
    this.userService.markAsRegisteredInNyc(this.user.value()!.id)
      .subscribe(() => {
        this.user.reload();
        this.registerInNycVisible.set(false)
      });
  }

  onNycProfileEditToggle() {
    if (this.editingNycProfile) {
      this.userService.updateNycProfileAuth(
        this.user.value()!.id,
        {
          email1: this.nycProfileForm()!.getRawValue().email1,
          email2: this.nycProfileForm()!.getRawValue().email2,
          username: this.nycProfileForm()!.getRawValue().username,
          password: this.nycProfileForm()!.getRawValue().password
        }
      ).subscribe(() => {
        this.editingNycProfile = false;
        this.user.reload();
        this.nycProfileForm()?.disable();
      });
    } else {
      this.editingNycProfile = true;
      this.nycProfileForm()?.enable();
    }
  }

  resetFailedApplications() {
    this.userService.resetFailedApplications(this.user.value()!.id).subscribe(
      () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Reset failed applications',
          detail: `User ${this.user.value()!.email} reset`
        });
      }
    );
  }

  deleteUser() {
    const result = confirm("Are you sure you want to delete this user? This action cannot be undone.");

    if (!result) {
      return;
    }

    this.userService.deleteUser(this.user.value()!.id).subscribe(
      () => {
        this.messageService.add({
          severity: 'success',
          summary: 'User deleted',
          detail: `User ${this.user.value()!.email} deleted`
        });

        this.router.navigate(['/users']);
      }
    )
  }

  onIgnoreToggle() {
    this.userService.toggleIgnore(
      this.user.value()!.id
    ).subscribe(
      () => {
        this.user.reload();
        this.messageService.add({
          severity: 'success',
          summary: 'Toggled ignore',
          detail: `User ${this.user.value()!.email} toggled ignore`
        });
      }
    )
  }

  retryEmail(s3Key: string) {
    const confirmation = window.confirm(
      `Are you sure you want to retry email with key ${s3Key}?`
    );
    if (!confirmation) {
      return;
    }
    this.emailService.retryEmail(
      s3Key
    ).subscribe(
      () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Email retried',
          detail: `Email with key ${s3Key} retried successfully`
        });
      },
    )
  }

  markCurrentProfileComplete() {
    this.userService.markCurrentProfileComplete(this.user.value()!.id).subscribe(
      () => {
        this.user.reload();
        this.messageService.add({
          severity: 'success',
          summary: 'Marked current profile complete',
          detail: `User ${this.user.value()!.email} current profile marked as complete`
        });
      }
    );
  }

  getEligibility() {
    const apartmentId = window.prompt("Apartment ID");

    if (!apartmentId) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Apartment ID is required'
      });
      return;
    }

    this.userService.checkEligibility(
      this.user.value()!.id,
      {
        apartmentId
      }).subscribe(
      (value) => {
        this.messageService.add({
          severity: 'success',
          summary: 'Eligibility checked',
          detail: value.result
        });
      },
    )
  }

  redistributeIncome() {
    const result = window.confirm("Are you sure?");

    if(!result) {
      return
    }

    this.userService.redistributeIncome(
      this.user.value()!.id
    ).subscribe(
      () => {
        this.user.reload();
        this.messageService.add({
          severity: 'success',
          summary: 'Redistributed income',
          detail: `User ${this.user.value()!.email} income redistributed`
        });
      }
    );
  }

  onEmailDownload(s3Key: string) {
    this.emailService.openEmail(s3Key);
  }

  deleteApplication(id: string) {
    this.confirmationService.confirm(
      {
        message: 'Are you sure you want to delete this application?',
        header: 'Delete Application',
        icon: 'pi pi-exclamation-triangle',
        accept: () => {
          this.applicationService.deleteApplication(id).subscribe(
            () => {
              this.user.reload();
              this.messageService.add(
                {severity: 'success', summary: 'Success', detail: 'Application deleted successfully'}
              );
            },
          );
        }
      }
    )
  }


  resyncApplication(id: string) {
    this.applicationService.resyncApplication(id).subscribe(
      () => {
        this.user.reload();
        this.messageService.add(
          {severity: 'success', summary: 'Success', detail: 'Application resynced successfully'}
        );
      },
    );
  }

  addMissingNycIds() {
    this.userService.addMissingNycIds(
      this.userId()
    ).subscribe(
      () => this.messageService.add(
        {severity: 'success', summary: 'Success', detail: 'Application NYC Id connect successfully'}
      )
    )
  }

  markApplicationAsNotSelected(id: string) {
    this.confirmationService.confirm(
      {
        message: 'Are you sure you want to mark this application as not selected?',
        header: 'Mark Application as Not Selected',
        icon: 'pi pi-exclamation-triangle',
        accept: () => this.applicationService.markApplicationAsNotSelected(id).subscribe(
          () => {
            this.user.reload();
            this.messageService.add(
              {severity: 'success', summary: 'Success', detail: 'Application marked as not selected successfully'}
            );
          },
        )
      }
    )
  }

  openStatusEventDialog(application: ApplicationDto) {
    if (!this.allowApplicationStatusEvent) {
      return;
    }

    this.selectedApplication.set(application);
    const parsedLogNr = this.parseLogNr(application);

    this.statusEventForm.reset({
      status: 'applied',
      lotteryNumber: parsedLogNr,
      reason: null,
      applicationMethod: application.method ?? null,
    });
    this.statusEventDialogVisible.set(true);
  }

  setStatus(status: (typeof this.statusOptions)[number]) {
    this.statusEventForm.controls.status.setValue(status);
  }

  setReason(reason: (typeof this.reasonOptions)[number]) {
    this.statusEventForm.controls.reason.setValue(reason);
  }

  setApplicationMethod(method: (typeof this.applicationMethodOptions)[number]) {
    this.statusEventForm.controls.applicationMethod.setValue(method);
  }

  setLotteryNumberFromLog(application: ApplicationDto) {
    const parsed = this.parseLogNr(application);
    if (parsed !== null) {
      this.statusEventForm.controls.lotteryNumber.setValue(parsed);
    }
  }

  setLotteryNumberRandom() {
    this.statusEventForm.controls.lotteryNumber.setValue(this.randomLotteryNumber());
  }

  emitStatusEvent() {
    const application = this.selectedApplication();
    if (!application) {
      return;
    }

    const status = this.statusEventForm.controls.status.getRawValue();
    let command: EmitApplicationStatusEventCommand | undefined;

    switch (status) {
      case 'applied':
        command = {
          status: 'applied',
          applicationMethod: this.statusEventForm.controls.applicationMethod.value
            ?? application.method
            ?? this.randomApplicationMethod(),
        };
        break;
      case 'pending-selection': {
        const lotteryNumberValue = this.statusEventForm.controls.lotteryNumber.value;
        const lotteryNumber = lotteryNumberValue === null
          ? (this.parseLogNr(application) ?? this.randomLotteryNumber())
          : Number(lotteryNumberValue);
        this.statusEventForm.controls.lotteryNumber.setValue(lotteryNumber);
        command = {
          status: 'pending-selection',
          lotteryNumber: lotteryNumber,
        };
        break;
      }
      case 'not-selected': {
        const lotteryNumberValue = this.statusEventForm.controls.lotteryNumber.value;
        const lotteryNumber = lotteryNumberValue === null
          ? (this.parseLogNr(application) ?? this.randomLotteryNumber())
          : Number(lotteryNumberValue);
        this.statusEventForm.controls.lotteryNumber.setValue(lotteryNumber);
        command = {
          status: 'not-selected',
          lotteryNumber: lotteryNumber,
          reason: this.statusEventForm.controls.reason.value ?? 'not-selected'
        };
        break;
      }
      case 'closed':
        command = {status: 'closed'};
        break;
    }

    if (!command) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Invalid command',
        detail: 'Could not build status event payload.'
      });
      return;
    }

    this.applicationService.emitApplicationStatusEvent(application.id, command).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Event emitted',
          detail: `Status event for application ${application.id} queued.`
        });
        this.statusEventDialogVisible.set(false);
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Failed to emit',
          detail: 'Could not emit status event.'
        });
      }
    });
  }

  openEligibleEventDialog() {
    if (!this.allowEligibleLotteryEvent) {
      return;
    }

    this.selectedLotteryIds.set(new Set());
    this.eligibleEventDialogVisible.set(true);
  }

  toggleLotterySelection(lotteryId: string) {
    const next = new Set(this.selectedLotteryIds());
    if (next.has(lotteryId)) {
      next.delete(lotteryId);
    } else {
      next.add(lotteryId);
    }
    this.selectedLotteryIds.set(next);
  }

  selectRandomOpenLottery() {
    const openLotteries = this.openLotteries();
    if (openLotteries.length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'No open lotteries',
        detail: 'Could not find an open lottery to select.'
      });
      return;
    }

    const randomLottery = openLotteries[Math.floor(Math.random() * openLotteries.length)];
    this.selectedLotteryIds.set(new Set([randomLottery.id]));
  }

  emitEligibleLotteryEvents() {
    if (!this.allowEligibleLotteryEvent) {
      return;
    }

    const selected = Array.from(this.selectedLotteryIds());

    if (selected.length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Select a lottery',
        detail: 'Pick at least one lottery before emitting.'
      });
      return;
    }

    const userId = this.userId();
    this.applicationService.emitNewEligibleLotteryEvents(userId, {lotteryIds: selected}).subscribe({
      next: (response) => {
        this.messageService.add({
          severity: 'success',
          summary: 'Event emitted',
          detail: `Queued ${response.count ?? selected.length} new-eligible-lottery event(s).`
        });
        this.eligibleEventDialogVisible.set(false);
        this.selectedLotteryIds.set(new Set());
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Failed to emit',
          detail: 'Could not emit new-eligible-lottery event.'
        });
      }
    });
  }

  protected selectedLotteryLabel(): string {
    return Array.from(this.selectedLotteryIds()).join(', ');
  }

  protected parseLogNr(application: ApplicationDto | undefined): number | null {
    if (!application?.logNr) {
      return null;
    }
    const parsed = Number(application.logNr);
    return Number.isNaN(parsed) ? null : parsed;
  }

  private randomLotteryNumber(): number {
    return Math.floor(Math.random() * 900000) + 100000;
  }

  protected randomApplicationMethod(): (typeof this.applicationMethodOptions)[number] {
    return this.applicationMethodOptions[Math.floor(Math.random() * this.applicationMethodOptions.length)];
  }

  onSync(command: ResetSyncStatusCommand) {
    this.userService.resetSyncStatus(
      this.user.value()!.id,
      command
    ).subscribe(
      () => {
        this.user.reload();
        this.messageService.add({
          severity: 'success',
          summary: 'Sync status reset',
          detail: `Sync status for ${command.type} reset successfully`
        });
      },
    );
  }

  addApartmentManually() {
    const apartmentId = window.prompt("Apartment ID");
    const nycApplicationId = window.prompt("NYC Application ID");

    if (!apartmentId || !nycApplicationId) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Apartment ID and NYC Application ID are required'
      });
      return;
    }

    this.userService.connectToNycApplication(this.user.value()!.id, {
      apartmentId,
      nycId: Number(nycApplicationId)
    }).subscribe(
      () => {
        this.user.reload();
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Apartment connected to NYC application successfully'
        });
      },
    );
  }

  unlockEmailTwo() {
    this.userService.unlockWaitingForEmailTwo(
      this.user.value()!.id
    ).subscribe(
      () => {
        this.user.reload();
        this.messageService.add({
          severity: 'success',
          summary: 'Unlocked waiting for email two',
          detail: `User ${this.user.value()!.id} unlocked`
        });
      }
    )
  }

  triggerScheduledResync() {
    this.userService.triggerScheduledResync(this.user.value()!.id).subscribe(
      () => {
        this.user.reload();
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Scheduled resync triggered successfully'
        });
      },
    );
  }

  syncUserWithNyc() {
    const userView = this.user.value();
    if (!userView) {
      return;
    }

    this.syncingUserWithNyc.set(true);
    this.userService.syncUserWithNyc(userView.id).pipe(
      finalize(() => this.syncingUserWithNyc.set(false)),
    ).subscribe({
      next: (result) => {
        this.user.reload();
        this.messageService.add({
          severity: result === 'logged-in' ? 'success' : 'warn',
          summary: 'Manual NYC sync triggered',
          detail: result === 'logged-in'
            ? 'NYC data will be refreshed shortly.'
            : 'No login was required for this sync.'
        });
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Manual NYC sync failed',
          detail: 'Unable to invoke the NYC sync lambda.'
        });
      }
    });
  }

  markAsPaid() {
    const type = window.prompt("Type");
   if(!type || !['revenue-cat', 'stripe'].includes(type)) {
     throw new Error("Invalid type. Must be 'revenue-cat' or 'stripe'.");
   }

    this.userService.markAsPaid(this.user.value()!.id, {type: type as 'revenue-cat' | 'stripe'}).subscribe(
      () => {
        this.user.reload();
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'User marked as paid'
        });
      },
    );
  }

  onClearNycId(command: ClearIncomeNycIdCommand) {
    this.userService.clearMemberIncomeNycId(
      this.user.value()!.id,
      command
    ).subscribe(
      () => {
        this.user.reload();
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: `NYC ID cleared for household member ${command.householdMemberId}`
        });
      },
    );
  }

  editNycAuth() {
    const confirmed = confirm("User is already registered in NYC. Are you sure you want to edit the NYC auth details?");
    if(!confirmed) {
      return;
    }
    this.nycAuth.set({
      email1: this.user.value()!.nycProfile!.mail1,
      email2: this.user.value()!.nycProfile!.mail2,
      username: this.user.value()!.nycProfile!.username,
      password: this.user.value()!.nycProfile!.password,
      firstName: this.user.value()!.firstName,
      lastName: this.user.value()!.lastName,
    })
    this.nycProfileForm()?.enable();
    this.registerInNycVisible.set(true);
    this.editingNycProfile = true;
  }

  verifyHousehold() {
    const userView = this.user.value();

    if (!userView || userView.nycProfile?.status !== 'complete') {
      this.messageService.add({
        severity: 'warn',
        summary: 'Cannot verify NYC household',
        detail: 'NYC profile must be complete before verification.'
      });
      return;
    }

    this.verifyingHousehold.set(true);
    this.userService.verifyNycHousehold(userView.id).subscribe({
      next: (result) => {
        this.householdVerificationResult.set(result);
        if (result.status === 'ok') {
          const issueCount = result.issues.length;
          this.messageService.add({
            severity: issueCount === 0 && result.householdComplete ? 'success' : 'warn',
            summary: 'Verification complete',
            detail: issueCount === 0 && result.householdComplete
              ? 'NYC household matches and is complete.'
              : `${issueCount} issue(s) reported.`
          });
        } else {
          this.messageService.add({
            severity: result.status === 'error' ? 'error' : 'warn',
            summary: 'Verification skipped',
            detail: result.reason
          });
        }
      },
      error: () => {
        this.verifyingHousehold.set(false);
        this.messageService.add({
          severity: 'error',
          summary: 'Verification failed',
          detail: 'Could not verify NYC household.'
        });
      },
      complete: () => {
        this.verifyingHousehold.set(false);
      }
    });
  }

  reconnectHousehold() {
    this.userService.reconnectHousehold(this.user.value()!.id).subscribe(
      () => {
        this.user.reload();
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Household reconnected successfully'
        });
      },
    )
  }

  openHC() {
    this.userService.loginWith(
      this.user.value()!.id
    ).subscribe();
  }

  openNotificationTestPage() {
    this.router.navigate(['/users', this.userId(), 'notification-test']);
  }

  openApplicationCreatePage() {
    this.router.navigate(['/users', this.userId(), 'application-create']);
  }

  sendTextMessage() {
    const userView = this.user.value();

    if (!userView) {
      return;
    }

    if (!userView.smsNotificationsEnabled || !userView.phone) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Cannot send text',
        detail: 'User is not set up for text messages.'
      });
      return;
    }

    const message = window.prompt('Enter the text message to send to the user:');

    if (message === null) {
      return;
    }

    const trimmed = message.trim();

    if (!trimmed) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Message required',
        detail: 'Text message cannot be empty.'
      });
      return;
    }

    const confirmed = window.confirm(`Send the following text to ${userView.phone}?\n\n${trimmed}`);

    if (!confirmed) {
      return;
    }

    this.userService.sendCustomNotification(
      userView.id,
      {textMessage: trimmed}
    ).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Text message queued',
          detail: 'Notification created successfully.'
        });
        this.user.reload();
      },
      error: (error) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Failed to send text',
          detail: error?.error?.message ?? 'Unable to send text message.'
        });
      }
    });
  }
}
